// Two of the modal cards over the scrim (round 083 card 16), as pure
// markup: the keyboard reference (plan §5.1) and the media player (plan
// §5). `ui/overlay.ts` owns the cards, the scrim and the presses.

import { overlay as S } from '../../gen/strings';
import { mmss } from '../../locale/clock';
import { esc } from '../esc';
import { textGlyph } from '../glyph';
import { shownKeymap } from '../keymap';
import type { FeatureFlag } from '../../gen/featureflags';
import type { UIAudio } from './types';

/**
 * The keyboard reference: static markup, built again on each open, because
 * a row under a flag stands only while the world latched it (R2870).
 * A hidden binding is documented in ui-map-interface.md §9 and not here
 * (R456), and a group whose rows are all hidden drops its heading too:
 * an empty LAYERS heading would advertise the very keys the panel is told
 * to leave out.
 */
export function keysPanelHtml(has: (flag: FeatureFlag) => boolean = () => false): string {
  return `<div class="title">${S.keys_title}`
    + `<button data-act="keysclose" title="${S.close_title}">✕</button></div>`
    // A row under a flag the world did not latch is left out (R2870).
    + shownKeymap().map((g) => ({ g, keys: g.keys.filter((k) => !k.hidden && (k.flag === undefined || has(k.flag))) }))
      .map(({ g, keys }) => {
        if (keys.length === 0) return '';
        const rows = keys.map((k) => `<div class="row"><span class="key">${esc(k.key)}</span>`
          + `<span class="does">${esc(k.does)}</span></div>`).join('');
        // The heat-map rows fold under their head (R2184): a native
        // fold, so a press, Enter and Space open it with no state.
        return g.fold
          ? `<details class="kfold"><summary class="group">${esc(g.name)}`
            + ` <i>${S.keys_fold_count({ n: keys.length })}</i></summary>${rows}</details>`
          : `<div class="group">${esc(g.name)}</div>${rows}`;
      }).join('')
    + `<div class="foot">${S.keys_foot}</div>`;
}

/**
 * The media player card: the head with the playlist and the close, then
 * the player. Everything drawn here comes from the feed `setAudio` pushes:
 * the overlay never touches the audio director, and every button leaves
 * as one `audioOp`.
 */
export function audioCardHtml(st: UIAudio['status']): string {
  return `<div class="ihead"><span class="ititle">`
    + `${esc(st?.playlistTitle ?? S.audio_title)}</span>`
    + `<button data-act="audioclose" title="${S.close_title}">✕</button></div>`
    + audioBodyHtml(st);
}

/**
 * The player under the head: the tracks, the transport, the clock and
 * the bar. The card above draws it, and so does the player page of the
 * ☸ OPTIONS menu under AUDIO (R1731). The bar's width is quantized to
 * whole percent, so the markup changes about once a second inside the
 * 4 Hz render, not on every frame that the clock moved a hair. Every
 * transport glyph asks for its text form (R579, R852), or the phone
 * draws ⏮ ⏸ ⏹ ⏭ as coloured emoji beside a text-drawn ⤨.
 */
export function audioBodyHtml(st: UIAudio['status']): string {
  let html = '';
  if (!st || st.tracks.length === 0) {
    html += `<div class="isub">${S.audio_empty}</div>`;
  } else {
    html += `<div class="isub">${S.audio_tracks({
      count: st.tracks.length,
    })}</div>`
      + st.tracks.map((t, i) => `<button class="mrow${i === st.trackIndex ? ' active' : ''}"`
        + ` data-act="mtrack" data-n="${i}">`
        + `<span class="mn">${String(i + 1).padStart(2, '0')}</span>`
        + `<span class="mt">${esc(t.title)}</span></button>`).join('')
      + `<div class="mtrans">`
      + `<button data-act="mprev" title="${S.audio_prev_title}">${textGlyph('⏮')}</button>`
      + (st.playing
        ? `<button data-act="mpause" class="primary" title="${S.audio_pause_title}">${textGlyph('⏸')}</button>`
        : `<button data-act="mplay" title="${S.audio_play_title}">${textGlyph('▶')}</button>`)
      + `<button data-act="mstop" title="${S.audio_stop_title}">${textGlyph('⏹')}</button>`
      + `<button data-act="mnext" title="${S.audio_next_title}">${textGlyph('⏭')}</button>`
      + `<button data-act="mshuffle" title="${S.audio_shuffle_title}"`
      + `${st.shuffle ? ' class="primary"' : ''}>${textGlyph('⤨')}</button>`
      + `</div>`;
    const pct = st.duration > 0
      ? Math.max(0, Math.min(100, Math.round((st.time / st.duration) * 100))) : 0;
    html += `<div class="mtime"><span>${esc(mmss(st.time))}</span>`
      + `<span>${esc(mmss(st.duration))}</span></div>`
      + `<div class="mbar"><span class="mfill" style="width:${pct}%"></span></div>`;
  }
  return html + `<div class="foot">${S.audio_foot}</div>`;
}
